import _, { get as _get } from 'lodash'
import {
  DecodedItem,
  GroupedItemsByProduct,
  ShippingParcelChecklist,
  SimpleReadingListBatchValidateResEntry,
  SimpleReadingListEntry,
  TmrProduct as StwProduct,
  TmrTag
} from 'stylewhere/api'
import { FormSchemaData, FormSchemaRouteProps, OperationConfig, RfidReader } from 'stylewhere/shared'
import { enabledWamGroupedProduct, showToastError } from 'stylewhere/utils'

/** Stato base condiviso da tutte le route di Operation Reading */
export type OperationReadingState = {
  items: DecodedItem[]
  loading: boolean
  formData: FormSchemaData
  tags?: TmrTag[]
  detected?: number
  filterParams?: Record<string, any>
}

/** Prop condivise da tutte le route di Operation Reading */
export type OperationReadingProps<State = OperationReadingState> = FormSchemaRouteProps<
  { opCode: string; id?: string },
  State
>

export class OperationReadingProvider {
  static currentTag
  static processedItems
  static tagsRead

  static async init(
    operation: OperationConfig,
    locationState: Partial<OperationReadingState>,
    backCallback: () => void,
    customFunction?: () => void,
    disabledFormData?: boolean
  ) {
    if (!disabledFormData && !locationState?.formData) {
      backCallback()
      return
    }

    try {
      await RfidReader.initialize()
      customFunction?.()
      // Aggiungi tags eventualmente letti durante la start
      locationState?.tags?.forEach((tag) => RfidReader.emulateTag(tag))
      operation.autostartAntenna && RfidReader.start()
    } catch (err) {
      showToastError(err)
      backCallback()
    }
  }

  static checkEntryStatus(operation: OperationConfig, entry: SimpleReadingListBatchValidateResEntry) {
    if (entry.statuses?.some((status) => operation.itemStatuses.ignore.includes(status))) {
      entry.status = 'ignored'
    } else if (entry.statuses?.some((status) => operation.itemStatuses.ignoreWithReason.includes(status)))
      entry.status = 'ignoredWithReason'
    else if (entry.statuses?.some((status) => operation.itemStatuses.error.includes(status))) entry.status = 'error'
    else if (entry.statuses?.some((status) => operation.itemStatuses.warning.includes(status))) entry.status = 'warning'
    // else if (!entry.data) entry.status = 'unexpected'

    return entry
  }

  static groupEntries(entries: SimpleReadingListBatchValidateResEntry[]) {
    const groupedEntries = new Map<string, SimpleReadingListBatchValidateResEntry>()
    entries.forEach((entry) => {
      if (entry.data !== null) {
        const code = entry.data.code
        if (groupedEntries.has(code)) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const existingEntry = groupedEntries.get(code)
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          existingEntry.detected++
          groupedEntries.set(code, existingEntry)
        } else {
          groupedEntries.set(code, { ...entry, detected: 1 })
        }
      }
    })

    return Array.from(groupedEntries.values())
  }

  static setDetectedQty(
    entries: SimpleReadingListBatchValidateResEntry[],
    allEntries: SimpleReadingListEntry[]
  ): SimpleReadingListBatchValidateResEntry[] {
    allEntries.forEach((entry) => {
      const code = entry.code
      const existingEntry = entries.find((e) => e.data?.code === code)
      if (existingEntry) entry.detected = existingEntry.detected ?? 0
    })
    return allEntries.map((entry) => {
      return {
        ...entry,
        data: {
          code: entry.code
        },
        type: '',
        statuses: [],
        status: ''
      }
    })
  }

  static checkItemStatus(operation: OperationConfig, item: DecodedItem<string>) {
    const invalidIdentifier = !item.item
    let isIgnored = item.statuses?.some((status) => operation.itemStatuses.ignore.includes(status))
    const isIgnoreWithReason = item.statuses?.some((status) => operation.itemStatuses.ignoreWithReason.includes(status))
    const isError = item.statuses?.some((status) => operation.itemStatuses.error.includes(status))
    const isWarning = item.statuses?.some((status) => operation.itemStatuses.warning.includes(status))
    // se il tag non è presente in nessuno stato si reimposta eventuale flag invalidIdentifier per inserirlo di default tra gli ignorati
    if (invalidIdentifier && !isIgnored && !isIgnoreWithReason && !isError && !isWarning) {
      isIgnored = true
    }

    // se item non presente setto con oggetto vuoto
    if (!item.item) {
      item.item = {} as any //Forzo item non a sistema item
    }

    if (isIgnored) {
      item.item.status = 'ignored'
      return item
    }

    if (isIgnoreWithReason) item.item.status = 'ignoredWithReason'
    else if (isError) item.item.status = 'error'
    else if (isWarning) item.item.status = 'warning'
    return item
  }

  static async processSingleItems(
    decodedItem: DecodedItem,
    operation: OperationConfig,
    formData: FormSchemaData,
    extensions?: any
  ) {
    let processedItem = OperationReadingProvider.checkItemStatus(operation, decodedItem)
    if (!processedItem.item || processedItem.item.status === 'ignored') {
      return processedItem
    } else if (processedItem.item.status === 'ignoredWithReason') {
      return processedItem
    } else if (!this.processedItems.some((itm) => itm.item !== null && itm.item?.id === processedItem.item?.id)) {
      if (extensions) processedItem = await extensions.processItem(operation, formData ?? {}, processedItem)
      if (processedItem && processedItem.item !== null) {
        return processedItem
      }
    }
    return undefined
  }

  static async processItems(
    operation: OperationConfig,
    itemMapFromReader: { [tagCode: string]: DecodedItem },
    formData: FormSchemaData,
    extensions?: any
  ) {
    const keys = Object.keys(itemMapFromReader)
    if (this.currentTag < keys.length - 1) {
      this.currentTag++
      const decodedItem = itemMapFromReader[this.tagsRead[this.currentTag]]
      const processedItem = await this.processSingleItems(decodedItem, operation, formData, extensions)
      if (processedItem) {
        processedItem.identifierCode = this.tagsRead[this.currentTag]
        this.processedItems.push(processedItem)
      }
      return this.processItems(operation, itemMapFromReader, formData, extensions)
    }
    return this.processedItems
  }

  static async processDecodedItems<T extends DecodedItem[] = DecodedItem[]>(
    operation: OperationConfig,
    itemMapFromReader: { [tagCode: string]: DecodedItem },
    items: T,
    formData: FormSchemaData,
    extensions?: any
  ) {
    this.currentTag = -1
    this.tagsRead = []
    this.processedItems = []
    for (const element of items) {
      this.processedItems.push(element)
    }
    const keys = Object.keys(itemMapFromReader)
    keys.forEach((tagCode) => {
      this.tagsRead.push(tagCode)
    })
    await this.processItems(operation, itemMapFromReader, formData, extensions)
    if (this.processedItems.length > 1 && operation.enabledCheckItemZone) {
      const itemZoneBase = this.processedItems[0].zone ? this.processedItems[0].zone.id : ''
      if (itemZoneBase !== '') {
        let itemZone, itemProcessed
        for (let i = 1; i < this.processedItems.length; i++) {
          itemProcessed = this.processedItems[i]
          itemZone = itemProcessed?.zone ? itemProcessed.zone.id : ''
          if (itemZone !== '' && itemZone !== itemZoneBase && itemProcessed.item) {
            itemProcessed.item.status = 'warning'
          }
        }
      }
    }
    return this.processedItems
  }

  static removeItem<T extends DecodedItem>(decodedItem: T, items: T[]) {
    const { item, identifierCode } = decodedItem
    if (!item && !identifierCode) return items

    const idx = items.findIndex((i) => item?.id === i.item?.id || identifierCode === i?.identifierCode)
    if (idx > -1) {
      items.splice(idx, 1)
      if (item) RfidReader.removeTags(item.identifiers.map(({ code }) => code))
      if (
        identifierCode &&
        (!item || (item && !item.identifiers.some((id) => !!id.code && id.code === identifierCode)))
      )
        RfidReader.removeTags([identifierCode])
    }
    return items
  }

  static groupItems(
    items: DecodedItem[],
    groupByProduct: boolean,
    operation?: OperationConfig,
    extension?: any,
    checklist?: ShippingParcelChecklist | null,
    products?: { [upc: string]: StwProduct },
    useDisplayMode?: boolean,
    productKey?: string
  ) {
    let isUnexpected
    let row
    const useDisplay = useDisplayMode && operation?.readingsDisplayMode === 'groupedByProduct'
    const productKeyFindValue = productKey || (operation?.options?.checklistMode === 'shipment' ? 'code' : 'id')

    let rowsByProduct: GroupedItemsByProduct[] = []
    if (operation?.readingsDisplayMode === 'groupedByProduct' || operation?.readingsDisplayMode === 'item') {
      const updateExpecteds = (
        productCode: string,
        quantity?: number,
        itemId?: string,
        detected?: number,
        productKeySearch = 'id'
      ) => {
        if (!products) return
        const product = products?.[productCode]
        if (!product) {
          throw new Error(`Checklist product ${productCode} missing from parcel`)
        }

        let productKeyValue = productCode
        let productKeyFilter = 'code'
        let productKeyFind = productKeySearch
        if (productKey && products[productCode] && _get(products[productCode], productKey)) {
          productKeyValue = _get(products[productCode], productKey)
          productKeyFilter = productKey
          productKeyFind = productKey
        }
        const relatedItems =
          !groupByProduct && !useDisplay
            ? []
            : items.filter(
                (itm) =>
                  itm.item?.product &&
                  _get(itm.item?.product, productKeyFilter) === productKeyValue &&
                  itm.item?.status !== 'ignored' &&
                  itm.item?.status !== 'ignoredWithReason'
              )

        if (!groupByProduct && !useDisplay) {
          row = {
            item: { id: itemId, product: product },
            detected: detected ?? 0,
            expected: 0,
            unexpected: 0,
            items: relatedItems,
            status: undefined
          }
          rowsByProduct.push(row)
        } else {
          row = rowsByProduct.find((p) => {
            const productP = p.product
            const _getProductKey = _get(productP, productKeyFind)
            const _getProductKeyProduct = _get(product, productKeyFind)
            return _getProductKey === _getProductKeyProduct
          })
          if (!row) {
            row = {
              product,
              detected: detected ?? 0,
              expected: 0,
              unexpected: 0,
              items: relatedItems,
              status: undefined
            }
            rowsByProduct.push(row)
          }
        }
        row.expected += quantity ?? 1
      }
      if (checklist && checklist !== null) {
        const checklistItems = checklist.checklistItems ?? []
        if (groupByProduct) {
          if (['CHECKLIST_BY_ITEM', 'MIXED_CHECKLIST'].includes(checklist.type)) {
            checklistItems.forEach(({ productCode, itemId, detected }) =>
              updateExpecteds(productCode, 1, itemId, detected ?? 0, productKeyFindValue)
            )
          }

          if (['CHECKLIST_BY_PRODUCT', 'MIXED_CHECKLIST'].includes(checklist.type)) {
            const checklistProducts = checklist.checklistProducts ?? checklist.products ?? []
            checklistProducts.forEach(({ productCode, quantity = 1, detected = 0 }) =>
              updateExpecteds(productCode, quantity, undefined, detected ?? 0, productKeyFindValue)
            )
          }
        } else {
          checklistItems.forEach(({ productCode, itemId, detected }) =>
            updateExpecteds(productCode, 1, itemId, detected ?? 0, productKeyFindValue)
          )
        }
      }

      const productKeyFind = productKey || (operation?.options?.checklistMode === 'shipment' ? 'code' : 'id')

      items.forEach((decodedItem) => {
        if (
          !decodedItem.item?.product ||
          decodedItem.item.status === 'ignored' ||
          decodedItem.item.status === 'ignoredWithReason'
        )
          return
        const itemIntrinsicQuantity = this.getItemIntrinsicQuantity(decodedItem, extension)
        isUnexpected = false
        if (groupByProduct) {
          const { product } = decodedItem.item
          if (enabledWamGroupedProduct()) {
            if (decodedItem.item?.attributes?.wam) {
              if (!product.attributes) {
                product.attributes = {}
              }
              product.attributes.wam = decodedItem.item.attributes.wam
              row = rowsByProduct.find((p) => p.product.attributes?.wam === product.attributes?.wam)
            } else {
              row = rowsByProduct.find(
                (p) => p.product && _get(p.product, productKeyFind) === _get(product, productKeyFind)
              )
            }
          } else {
            row = rowsByProduct.find(
              (p) => p.product && _get(p.product, productKeyFind) === _get(product, productKeyFind)
            )
          }

          if (row) {
            row.detected += itemIntrinsicQuantity

            // Push new decodeItem only if do not exist item with same identifier
            if (!row.items.reduce((exist, itm) => itm.item.id === decodedItem?.item?.id || exist, false)) {
              row.items.push(decodedItem)
            }
          } else {
            row = {
              product,
              detected: itemIntrinsicQuantity,
              expected: 0,
              unexpected: 0,
              items: [decodedItem],
              status: undefined,
              added: true
            }
            isUnexpected = true
          }
        } else {
          if (useDisplay) {
            const { product } = decodedItem.item
            if (enabledWamGroupedProduct()) {
              if (decodedItem.item?.attributes?.wam) {
                if (!product.attributes) {
                  product.attributes = {}
                }
                product.attributes.wam = decodedItem.item.attributes.wam
                row = rowsByProduct.find((p) => p.item?.product.attributes?.wam === product.attributes?.wam)
              } else {
                row = rowsByProduct.find(
                  (p) => p.item?.product && _get(p.item.product, productKeyFind) === _get(product, productKeyFind)
                )
              }
            } else {
              row = rowsByProduct.find(
                (p) => p.item?.product && _get(p.item.product, productKeyFind) === _get(product, productKeyFind)
              )
            }
          } else {
            row = rowsByProduct.find((p) => p.item?.id === decodedItem.item?.id)
          }
          if (row) {
            row.detected += itemIntrinsicQuantity
            if (useDisplay) {
              const relatedItem = row.items.find((item) => item.item.id === decodedItem.item?.id)
              if (!relatedItem) row.items.push(decodedItem)
            }
          } else {
            row = {
              item: decodedItem.item,
              statuses: decodedItem.statuses,
              detected: itemIntrinsicQuantity,
              expected: 0,
              unexpected: 0,
              items: [],
              status: undefined,
              added: true
            }
            isUnexpected = true
          }
        }

        row.unexpected =
          isUnexpected || (row.expected > 0 && row.detected > row.expected ? row.detected - row.expected : 0)

        // Stato
        if (decodedItem.item.status === 'error') {
          row.status = 'error'
        } else if (decodedItem.item.status === 'warning' && row.status !== 'error') {
          row.status = 'warning'
        }

        if (isUnexpected) {
          rowsByProduct.push(row)
        }
      })
      rowsByProduct = rowsByProduct.sort((a) => (a.status === 'error' ? -1 : a.status === 'warning' ? 0 : 1))
      return (
        rowsByProduct
          // Processa le righe con l'extension (se ritorna null la riga deve essere saltata)
          .map((rowData) => (extension ? extension.processRow(operation, rowData) : rowData))
          .filter((rowData): rowData is GroupedItemsByProduct => rowData !== null && row)
      )
    }
    throw new Error(`Unsupported operation readingsDisplayMode ${operation?.readingsDisplayMode}`)
  }

  static getItemIntrinsicQuantity(item: DecodedItem, extension: any) {
    if (!extension?.itemIntrinsicQuantityField) {
      if (item.detected) return item.detected
      return 1
    }
    return Number(_.get(item, extension.itemIntrinsicQuantityField, 1))
  }
}
